import { CodeBlock } from '@ui/pages/onboarding/components/configuration/CodeBlock'

import { DATABLE_API_KEY } from './constants'
import { ConfigurationProps } from './types'

export const OpenTelemetryConfiguration = ({ instanceUrl, datableApiKey }: ConfigurationProps) => (
  <>
    <h1 className="my-4 text-3xl font-bold">OpenTelemetry Collector and Agents</h1>

    <p className="my-4 text-gray-500">
      OpenTelemetry is a Swiss Army knife for your app's health - minus the tiny, easy-to-lose toothpick. It's a
      collection of APIs, SDKs, standards, and tools to let you peek under the hood of your app, all without vendor
      lock-in.
    </p>

    <p className="my-4 text-gray-500">
      This doc is focused on how to set up sending OpenTelemetry data to Datable from applications or from the
      OpenTelemetry collector.
    </p>

    <h2 className="my-4 text-2xl font-bold">Configuring the OpenTelemetry collector</h2>

    <p className="my-4 text-gray-500">
      Configuration can be configured in{' '}
      <a
        href="https://opentelemetry.io/docs/collector/configuration/"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        many ways
      </a>
      . We are focused on modifying the{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">src/opentelemetry</code> file within your
      OpenTelemetry installation. Configuration is in two parts - setting up an exporter, and adding that exporter to
      your pipelines.
    </p>

    <h3 className="my-4 text-xl font-bold">Exporter</h3>

    <p className="my-4 text-gray-500">Defining your exporter can be done two ways:</p>

    <h4 className="text-l my-4 font-bold">HTTP</h4>

    <CodeBlock
      code={`
exporters:
  otlphttp/datable:
    endpoint: https://${instanceUrl}:4318
    headers:
      "${DATABLE_API_KEY}": "${datableApiKey}"
`}
    />

    <h4 className="text-l my-4 font-bold">gRPC</h4>

    <CodeBlock
      code={`
exporters:
  otlp/datable:
    endpoint: ${instanceUrl}:4317
    headers:
      "${DATABLE_API_KEY}": "${datableApiKey}"
`}
    />

    <p className="my-4 text-gray-500">
      More examples are on{' '}
      <a
        href="https://github.com/open-telemetry/opentelemetry-collector/blob/main/exporter/README.md#configuring-exporters"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        the OTel website
      </a>
      .
    </p>

    <h3 className="my-4 text-xl font-bold">Pipeline</h3>

    <p className="my-4 text-gray-500">
      Whichever exporter you choose, gRPC or HTTP, you need to add to the exporters section of your pipeline. Adding
      both will result in data being duplicated and sent twice.
    </p>

    <h4 className="text-l my-4 font-bold">HTTP</h4>

    <CodeBlock
      code={`
service:
  pipelines:
    traces:
      receivers: [otlp]
      processors: [batch]
      exporters: [otlphttp/datable]
    logs:
      receivers: [otlp]
      processors: [batch]
      exporters: [otlphttp/datable]
`}
    />

    <h4 className="text-l my-4 font-bold">gRPC</h4>

    <CodeBlock
      code={`
service:
  pipelines:
    traces:
      receivers: [otlp]
      processors: [batch]
      exporters: [otlp/datable]
    logs:
      receivers: [otlp]
      processors: [batch]
      exporters: [otlp/datable]
`}
    />

    <h3 className="my-4 text-xl font-bold">After configuration</h3>

    <p className="my-4 text-gray-500">
      Ensure you restart your OpenTelemetry Collector and check to see if your logs are flowing into Datable.
    </p>

    <h3 className="my-4 text-xl font-bold">Full example</h3>

    <p className="my-4 text-gray-500">
      This is the config we use for local development. It's worth calling out the{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">debug</code> exporter, which will display in
      standard out.
    </p>

    <CodeBlock
      code={`
receivers:
  otlp:
    protocols:
      grpc:
        endpoint: 0.0.0.0:4317
      http:
        endpoint: 0.0.0.0:4318
        cors:
          allowed_origins:
            - "http://*"
            - "https://*"

exporters:
  debug:
  otlphttp/datable:
    endpoint: "https://${instanceUrl}:4318"

processors:
  batch:

connectors:
  spanmetrics:

service:
  pipelines:
    traces:
      receivers: [otlp]
      processors: [batch]
      exporters: [otlphttp/datable, debug]
    logs:
      receivers: [otlp]
      processors: [batch]
      exporters: [otlphttp/datable, debug]
`}
    />

    <h2 className="my-4 text-2xl font-bold">Instrumenting your applications with OpenTelemetry agents</h2>

    <p className="my-4 text-gray-500">
      OpenTelemetry can be instrumented through a variety of means depending on the specific needs and application
      stack. See the{' '}
      <a
        href="https://opentelemetry.io/docs/getting-started/"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        OpenTelemetry Getting Started
      </a>{' '}
      documentation for options and resources.
    </p>

    <h3 className="my-4 text-xl font-bold">Tracing</h3>

    <p className="my-4 text-gray-500">
      Import the below file at the start of your application. It includes auto-instrumentation, so the most common
      Node.js libraries will be automatically instrumented.
    </p>

    <p className="my-4 text-gray-500">Below is the minimal snippet we use internally, and configure with ENV VARS.</p>

    <CodeBlock
      code={`
/* tracer.ts */

import * as os from 'os'

import { getNodeAutoInstrumentations } from '@opentelemetry/auto-instrumentations-node'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-proto'
import { Resource } from '@opentelemetry/resources'
import * as opentelemetry from '@opentelemetry/sdk-node'
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions'

const resource = new Resource({
  [SemanticResourceAttributes.SERVICE_NAME]: 'my-app',
  [SemanticResourceAttributes.SERVICE_VERSION]: '0.0.1',
  [SemanticResourceAttributes.SERVICE_NAMESPACE]: 'my-namespace',
  [SemanticResourceAttributes.HOST_NAME]: os.hostname(),
})

const spanCollectorOptions = {
  url: "https://${instanceUrl}:4318/v1/traces",
};

const traceExporter = new OTLPTraceExporter(spanCollectorOptions)

const sdk = new opentelemetry.NodeSDK({
  resource,
  instrumentations: [getNodeAutoInstrumentations()],
  traceExporter: [traceExporter],
})

sdk.start()

process.on('SIGTERM', () => {
  sdk
    .shutdown()
    .then(
      () => console.log('SDK shut down successfully'),
      (err) => console.log('Error shutting down SDK', err)
    )
    .finally(() => process.exit(0))
})

`}
      language="typescript"
    />
  </>
)
